/**
 * The gunship's weapons, voiced (63.13; the rules are `gunshipMix.ts`, the audition sound_sheets/gunship.html): one-shots
 * built from the gunfire's parts (gunfire.ts `voice`, `chain`, `burst`, `tone`: the shared noise, its voice cap), each
 * with two styles on the sheet. `GUNSHIP_STYLE` holds the leans until the owner picks; 69.68's switch-on plays them for
 * every `gunshipFire` (game/actionSound.ts), where the .50's report and the LAW's launch were borrowed before.
 *   - **The cannons**, by calibre (s: 0 at 20 mm … 1 at 76 mm): the muzzle's crack lower, the body's thump deeper and
 *     longer, the air's push fuller, and from the 40 mm a second pressure wave behind the first.
 *     - `report`: just that.
 *     - `action`: the report with the gun's working heard close (`mechanismNear`): the 20 mm's feed rattling, the 30 mm
 *       chain gun's chug a round, the side gun's breech slamming shut on the next round and its case ringing out.
 *   - **The swarm** (a rocket every 0.08 s, 12 a salvo): `pop`, each rocket's small sharp launch over a short motor's
 *     hiss, a salvo a ripple of pops; or `hiss`, little pop and more motor, a salvo a tearing sheet of hiss.
 *   - **The heavy rocket:** `whoomp`, the LAW's launch made bigger; or `roar`, the ignition's crack and then the motor's
 *     long roar falling away as it leaves, a crackle in it.
 *   - **The pods:** `crack`, an unguided rocket's sharp launch and its full burn; or `thump`, a fatter, lower launch.
 * Cost: a sound 4–8 nodes for its length (a cannon's 0.3–1 s, the heavy rocket's roar 2.3 s); at the chin's 12 rounds a
 * second about 9 live at once, under the gunfire's voice cap (40). Presentation only.
 */
import { burst, chain, tone, voice, type Voice } from './gunfire';
import { duckMusic } from './sfx';
import { musicDuckFor, type Heard } from './gunfireMix';
import type { GunshipSoundKey } from './gunshipMix';
import type { CannonCalibre } from '../types';

export type CannonStyle = 'report' | 'action';
export type SwarmStyle = 'pop' | 'hiss';
export type HeavyStyle = 'whoomp' | 'roar';
export type PodsStyle = 'crack' | 'thump';
export interface GunshipStyle { cannon: CannonStyle; swarm: SwarmStyle; heavy: HeavyStyle; pods: PodsStyle }
export const CANNON_STYLES: readonly CannonStyle[] = ['report', 'action'];
export const SWARM_STYLES: readonly SwarmStyle[] = ['pop', 'hiss'];
export const HEAVY_STYLES: readonly HeavyStyle[] = ['whoomp', 'roar'];
export const PODS_STYLES: readonly PodsStyle[] = ['crack', 'thump'];
/** The leans (the sheet's defaults) until the owner picks. */
export const GUNSHIP_STYLE: GunshipStyle = { cannon: 'action', swarm: 'pop', heavy: 'roar', pods: 'crack' };

const lerp = (a: number, b: number, s: number) => a + (b - a) * s;

/** A cannon's report into `v`: `near` the mechanism's share heard (gunshipMix `mechanismNear`). */
function cannonInto(v: Voice, c: CannonCalibre, h: Heard, style: CannonStyle, near: number): void {
  const s = (c - 20) / 56;
  burst(v, { dur: lerp(0.05, 0.11, s), vol: 1, type: 'bandpass', freq: lerp(1600, 700, s), Q: 0.6 });   // the muzzle's crack
  tone(v, { dur: lerp(0.22, 0.72, s), vol: 0.85, freq: lerp(92, 42, s), to: lerp(34, 18, s) });         // the body's thump
  burst(v, { dur: lerp(0.1, 0.4, s), vol: lerp(0.6, 0.8, s), type: 'lowpass', freq: lerp(480, 280, s), freqTo: 120 });   // the air's push
  if (c >= 40) burst(v, { at: lerp(0.02, 0.04, s), dur: lerp(0.3, 0.7, s), vol: 0.45, type: 'lowpass', freq: 300, freqTo: 50 });   // the second pressure wave
  burst(v, { at: 0.01, dur: h.tail, vol: 0.28, type: 'lowpass', freq: lerp(700, 450, s) });   // the echo
  if (style !== 'action' || near < 0.02) return;
  if (c === 20) burst(v, { at: 0.03, dur: 0.04, vol: 0.18 * near, type: 'bandpass', freq: 2800, Q: 3 });   // the feed's rattle
  else if (c === 30) {   // the chain gun's chug: its bolt run forward on the next round
    tone(v, { at: 0.05, dur: 0.04, vol: 0.2 * near, freq: 520, to: 300, type: 'square' });
    burst(v, { at: 0.05, dur: 0.04, vol: 0.3 * near, type: 'bandpass', freq: 1900, Q: 5 });
  } else {   // the breech: thrown open by the recoil, slammed shut on the next round, the case rung out on the deck
    const slam = lerp(0.16, 0.28, (c - 40) / 36);
    tone(v, { at: slam, dur: 0.07, vol: 0.28 * near, freq: lerp(380, 260, s), to: lerp(230, 150, s), type: 'square' });
    burst(v, { at: slam, dur: 0.08, vol: 0.38 * near, type: 'bandpass', freq: 1400, Q: 4 });
    tone(v, { at: slam + 0.06, dur: 0.3, vol: 0.07 * near, freq: lerp(2400, 1500, s), to: lerp(2300, 1440, s), type: 'triangle' });
  }
}
function swarmInto(v: Voice, h: Heard, style: SwarmStyle): void {
  if (style === 'pop') {
    burst(v, { dur: 0.025, vol: 0.8, type: 'bandpass', freq: 2800, Q: 0.6 });
    tone(v, { dur: 0.09, vol: 0.35, freq: 150, to: 60 });
    burst(v, { at: 0.01, dur: 0.3, vol: 0.35, type: 'bandpass', freq: 1100, freqTo: 2600, Q: 1 });
  } else {
    burst(v, { dur: 0.012, vol: 0.4, type: 'highpass', freq: 3000 });
    burst(v, { dur: 0.55, vol: 0.7, type: 'bandpass', freq: 800, freqTo: 2000, Q: 0.8, attack: 0.02 });
    burst(v, { dur: 0.25, vol: 0.25, type: 'lowpass', freq: 260 });
  }
  burst(v, { at: 0.01, dur: h.tail * 0.6, vol: 0.15, type: 'lowpass', freq: 600 });
}
function heavyInto(v: Voice, h: Heard, style: HeavyStyle): void {
  if (style === 'whoomp') {
    burst(v, { dur: 0.04, vol: 0.9, type: 'bandpass', freq: 2000, Q: 0.5 });
    tone(v, { dur: 0.4, vol: 0.9, freq: 70, to: 28 });
    burst(v, { dur: 0.3, vol: 0.6, type: 'lowpass', freq: 600, freqTo: 200 });
    burst(v, { at: 0.02, dur: 1, vol: 0.4, type: 'bandpass', freq: 600, freqTo: 1800, Q: 0.8 });
  } else {
    burst(v, { dur: 0.03, vol: 0.85, type: 'bandpass', freq: 2400, Q: 0.5 });
    tone(v, { dur: 0.3, vol: 0.75, freq: 80, to: 32 });
    burst(v, { at: 0.02, dur: 2.2, vol: 0.55, type: 'bandpass', freq: 1000, freqTo: 600, Q: 0.5, attack: 0.06 });   // the motor, falling away
    burst(v, { at: 0.03, dur: 1.6, vol: 0.12, type: 'highpass', freq: 3500, attack: 0.05 });                        // its crackle
    burst(v, { at: 0.02, dur: 1.4, vol: 0.35, type: 'lowpass', freq: 220, attack: 0.05 });                          // its rumble
  }
  burst(v, { at: 0.01, dur: h.tail, vol: 0.22, type: 'lowpass', freq: 700 });
}
function podsInto(v: Voice, h: Heard, style: PodsStyle): void {
  if (style === 'crack') {
    burst(v, { dur: 0.03, vol: 0.9, type: 'bandpass', freq: 2600, Q: 0.5 });
    tone(v, { dur: 0.22, vol: 0.65, freq: 95, to: 38 });
    burst(v, { at: 0.02, dur: 0.12, vol: 0.5, type: 'lowpass', freq: 600 });
    burst(v, { at: 0.03, dur: 0.7, vol: 0.45, type: 'bandpass', freq: 900, freqTo: 2400, Q: 0.9 });
  } else {
    burst(v, { dur: 0.04, vol: 0.85, type: 'bandpass', freq: 1700, Q: 0.4 });
    tone(v, { dur: 0.3, vol: 0.85, freq: 70, to: 30 });
    burst(v, { dur: 0.3, vol: 0.55, type: 'lowpass', freq: 900, freqTo: 250 });
    burst(v, { at: 0.03, dur: 0.6, vol: 0.35, type: 'bandpass', freq: 700, freqTo: 1600, Q: 0.9 });
  }
  burst(v, { at: 0.01, dur: h.tail, vol: 0.22, type: 'lowpass', freq: 700 });
}

function soundInto(v: Voice, key: GunshipSoundKey, h: Heard, style: GunshipStyle, near: number): void {
  if (key === 'swarm') swarmInto(v, h, style.swarm);
  else if (key === 'heavy') heavyInto(v, h, style.heavy);
  else if (key === 'pods') podsInto(v, h, style.pods);
  else cannonInto(v, key, h, style.cannon, near);
}
/** How long a sound rings (s), for its voice. */
function lengthOf(key: GunshipSoundKey, h: Heard, style: GunshipStyle): number {
  if (key === 'heavy') return Math.max(style.heavy === 'roar' ? 2.3 : 1.1, h.tail + 0.05);
  return Math.max(0.75, h.tail + 0.05);
}

/** A gunship's sound into the game's SFX bus (`near`: gunshipMix `mechanismNear` of its distance). */
export function playGunship(key: GunshipSoundKey, h: Heard, near: number, style: GunshipStyle = GUNSHIP_STYLE): void {
  const v = voice(h, lengthOf(key, h, style));
  if (!v) return;
  duckMusic(musicDuckFor(h.gain));
  soundInto(v, key, h, style, near);
}
/** The sound sheet's look at it: rendered into `dest` of `ctx` (an offline or the sheet's context) from `at` seconds. */
export function renderGunship(ctx: BaseAudioContext, key: GunshipSoundKey, h: Heard, near: number, style: GunshipStyle = GUNSHIP_STYLE, at = 0.01, dest: AudioNode = ctx.destination): void {
  soundInto(chain(ctx, dest, h, at).v, key, h, style, near);
}
